import { MedplumClient } from '@medplum/core';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { config } from '../config';

/**
 * API Client for Spring Boot backend
 *
 * This client uses the Medplum access token for authentication,
 * enabling a unified auth experience where users authenticate once
 * via Auth0/Medplum and can access both Medplum FHIR APIs and
 * the Spring Boot backend APIs.
 */

let apiClient: AxiosInstance | null = null;
let medplumClientRef: MedplumClient | null = null;

/**
 * Initialize the API client with the Medplum client reference
 * This should be called once during app initialization
 */
export function initializeApiClient(medplum: MedplumClient): void {
  medplumClientRef = medplum;

  apiClient = axios.create({
    baseURL: config.api.url,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  apiClient.interceptors.request.use(
    async (requestConfig) => {
      if (medplumClientRef) {
        const accessToken = medplumClientRef.getAccessToken();
        if (accessToken) {
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }
      }
      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Handle 401 errors - token may be expired
      if (error.response?.status === 401) {
        console.warn('API returned 401 - token may be expired');
        // The Medplum client should handle token refresh
        // If that fails, it will trigger onUnauthenticated
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Get the initialized API client
 * Throws if client has not been initialized
 */
export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initializeApiClient first.');
  }
  return apiClient;
}

/**
 * Generic API request function
 */
async function request<T>(requestConfig: AxiosRequestConfig): Promise<T> {
  const client = getApiClient();
  const response: AxiosResponse<T> = await client.request(requestConfig);
  return response.data;
}

/**
 * API methods for Spring Boot backend
 * Add new endpoints as needed
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(url: string, params?: Record<string, unknown>): Promise<T> =>
    request<T>({ method: 'GET', url, params }),

  /**
   * POST request
   */
  post: <T>(url: string, data?: unknown): Promise<T> =>
    request<T>({ method: 'POST', url, data }),

  /**
   * PUT request
   */
  put: <T>(url: string, data?: unknown): Promise<T> =>
    request<T>({ method: 'PUT', url, data }),

  /**
   * PATCH request
   */
  patch: <T>(url: string, data?: unknown): Promise<T> =>
    request<T>({ method: 'PATCH', url, data }),

  /**
   * DELETE request
   */
  delete: <T>(url: string): Promise<T> =>
    request<T>({ method: 'DELETE', url }),
};

// Specific API modules can be added here
// Example:

/**
 * Patient Transfer API
 * Non-FHIR resource managed by Spring Boot backend
 */
export const patientTransferApi = {
  /**
   * Get all patient transfers
   */
  getAll: (): Promise<PatientTransfer[]> => api.get('/patient-transfers'),

  /**
   * Get a specific patient transfer
   */
  getById: (id: string): Promise<PatientTransfer> => api.get(`/patient-transfers/${id}`),

  /**
   * Create a new patient transfer
   */
  create: (data: CreatePatientTransferRequest): Promise<PatientTransfer> =>
    api.post('/patient-transfers', data),

  /**
   * Update a patient transfer
   */
  update: (id: string, data: UpdatePatientTransferRequest): Promise<PatientTransfer> =>
    api.put(`/patient-transfers/${id}`, data),
};

// Type definitions for backend APIs
// These should be moved to src/types/ as they grow

export interface PatientTransfer {
  id: string;
  patientId: string;
  fromProviderId: string;
  toProviderId: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed';
  createdAt: string;
  updatedAt: string;
}

export interface CreatePatientTransferRequest {
  patientId: string;
  fromProviderId: string;
  toProviderId: string;
  reason?: string;
}

export interface UpdatePatientTransferRequest {
  status?: 'pending' | 'approved' | 'rejected' | 'completed';
  reason?: string;
}

